import { apiClient } from './api/client';

export interface Permission {
  resource: string;
  actions: string[];
}

export interface Role {
  id: number;
  name: string;
  description: string | null;
  permissions: Permission[];
  created_at?: string;
  updated_at?: string;
}

export const rolesService = {
  /**
   * Récupère tous les rôles
   */
  async getAll(): Promise<Role[]> {
    const response = await apiClient.get<Role[]>('/roles');
    return response;
  },

  /**
   * Récupère un rôle par son ID
   */
  async getById(id: number): Promise<Role> {
    const response = await apiClient.get<Role>(`/roles/${id}`);
    return response;
  },

  /**
   * Crée un nouveau rôle
   */
  async create(data: {
    name: string;
    description?: string;
    permissions: Permission[];
  }): Promise<Role> {
    const response = await apiClient.post<Role>('/roles', data);
    return response;
  },

  /**
   * Met à jour un rôle
   */
  async update(id: number, data: Partial<Role>): Promise<Role> {
    const response = await apiClient.put<Role>(`/roles/${id}`, data);
    return response;
  },

  /**
   * Supprime un rôle
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/roles/${id}`);
  },

  /**
   * Récupère les utilisateurs ayant un rôle spécifique
   */
  async getUsersWithRole(roleId: number): Promise<any[]> {
    const response = await apiClient.get<any[]>(`/roles/${roleId}/users`);
    return response;
  }
};

// Permissions disponibles par ressource
export const AVAILABLE_PERMISSIONS = {
  products: {
    label: 'Produits',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'create', label: 'Créer' },
      { key: 'update', label: 'Modifier' },
      { key: 'delete', label: 'Supprimer' }
    ]
  },
  orders: {
    label: 'Commandes',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'update', label: 'Modifier' },
      { key: 'cancel', label: 'Annuler' }
    ]
  },
  customers: {
    label: 'Clients',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'create', label: 'Créer' },
      { key: 'update', label: 'Modifier' },
      { key: 'delete', label: 'Supprimer' }
    ]
  },
  invoices: {
    label: 'Factures',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'create', label: 'Créer' },
      { key: 'update', label: 'Modifier' },
      { key: 'delete', label: 'Supprimer' }
    ]
  },
  analytics: {
    label: 'Analytics',
    actions: [
      { key: 'view', label: 'Voir' }
    ]
  },
  cms: {
    label: 'CMS',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'create', label: 'Créer' },
      { key: 'update', label: 'Modifier' },
      { key: 'delete', label: 'Supprimer' }
    ]
  },
  settings: {
    label: 'Paramètres',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'update', label: 'Modifier' }
    ]
  },
  roles: {
    label: 'Rôles et Permissions',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'create', label: 'Créer' },
      { key: 'update', label: 'Modifier' },
      { key: 'delete', label: 'Supprimer' }
    ]
  },
  dashboard: {
    label: 'Dashboard',
    actions: [
      { key: 'view', label: 'Voir' }
    ]
  },
  transaction: {
    label: 'Transaction',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'update', label: 'Modifier' }
    ]
  },
  rapport: {
    label: 'Rapport',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'export', label: 'Exporter' }
    ]
  },
  code_promo: {
    label: 'Code Promo',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'create', label: 'Créer' },
      { key: 'update', label: 'Modifier' },
      { key: 'delete', label: 'Supprimer' }
    ]
  },
  categories: {
    label: 'Catégories',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'create', label: 'Créer' },
      { key: 'update', label: 'Modifier' },
      { key: 'delete', label: 'Supprimer' }
    ]
  },
  avis_produits: {
    label: 'Avis Produits',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'update', label: 'Modifier' },
      { key: 'delete', label: 'Supprimer' }
    ]
  },
  gestion_stock: {
    label: 'Gestion de Stock',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'update', label: 'Modifier' }
    ]
  },
  utilisateurs: {
    label: 'Utilisateurs',
    actions: [
      { key: 'view', label: 'Voir' },
      { key: 'create', label: 'Créer' },
      { key: 'update', label: 'Modifier' },
      { key: 'delete', label: 'Supprimer' }
    ]
  }
};

